import { v7 as uuidv7 } from 'uuid';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { InviteEntryPoint } from '@/features/RenterInvite/RenterInvite.types';

interface DirtyState {
  isDirty: boolean;
  dirtyFields: Record<string, unknown>;
}

// Ref lives outside the store to avoid re-renders when updated
const dirtyStateRef = { current: { isDirty: false, dirtyFields: {} } as DirtyState };

interface RenterInviteDrawerState {
  isOpen: boolean;
  formKey: number;
  flowId: string | null;
  entryPoint: InviteEntryPoint | null;
  newDealEmail: string | null;
  openDrawer: (entryPoint?: InviteEntryPoint) => void;
  openCleanRenterInviteDrawerForm: (entryPoint?: InviteEntryPoint) => void;
  closeDrawer: () => void;
  setNewDealEmail: (email: string | null) => void;
  clearNewDealEmail: () => void;
  dirtyStateRef: typeof dirtyStateRef;
  isFormDirty: () => boolean;
}

const useRenterInviteDrawerStore = create<RenterInviteDrawerState>()(
  devtools(
    () => ({
      isOpen: false as boolean,
      formKey: 0,
      flowId: null as string | null,
      entryPoint: null as InviteEntryPoint | null,
      newDealEmail: null as string | null,
      openDrawer: (entryPoint?: InviteEntryPoint) => {
        useRenterInviteDrawerStore.setState((state) => {
          if (state.isOpen) return state;
          dirtyStateRef.current = { isDirty: false, dirtyFields: {} };
          return {
            isOpen: true,
            formKey: state.formKey + 1,
            flowId: uuidv7(),
            entryPoint: entryPoint ?? null,
          };
        });
      },
      openCleanRenterInviteDrawerForm: (entryPoint?: InviteEntryPoint) => {
        dirtyStateRef.current = { isDirty: false, dirtyFields: {} };
        useRenterInviteDrawerStore.setState((state) => ({
          isOpen: true,
          formKey: state.formKey + 1,
          flowId: uuidv7(),
          entryPoint: entryPoint ?? null,
        }));
      },
      closeDrawer: () => {
        dirtyStateRef.current = { isDirty: false, dirtyFields: {} };
        useRenterInviteDrawerStore.setState({ isOpen: false, flowId: null, entryPoint: null });
      },
      setNewDealEmail: (email: string | null) => {
        useRenterInviteDrawerStore.setState({ newDealEmail: email });
      },
      clearNewDealEmail: () => {
        useRenterInviteDrawerStore.setState({ newDealEmail: null });
      },
      dirtyStateRef,
      isFormDirty: () => {
        const { isDirty, dirtyFields } = dirtyStateRef.current;
        // isDirty can have false positives with shouldUnregister: true (isDirty=true but dirtyFields={})
        // Only consider form dirty if isDirty AND there are actual dirty fields
        const hasDirtyFields = Object.keys(dirtyFields).length > 0;
        return isDirty && hasDirtyFields;
      },
    }),
    { name: 'renter-invite-drawer-store' },
  ),
);

export default useRenterInviteDrawerStore;
